import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type RoundSummary } from '../lib/api';
import '../styles/play.css';

/* The premade picker: /real-or-fake/modes/series
 *
 * Only series marked READY TO RECORD appear. That makes the status dropdown the hand-over
 * switch: Alyssa and Jason decide when something is finished enough for Adorian to walk into,
 * and a half-written draft cannot be picked up by mistake seconds before a take.
 *
 * Newest first — the one just handed over is the one most likely wanted.
 */

/** Only this status is offered. Draft is unfinished, recorded is done, archived is put away. */
const PLAYABLE = 'ready';

export default function ModesSeriesList() {
  const navigate = useNavigate();
  const [rounds, setRounds] = useState<RoundSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const all = await api.listRounds();
        if (!cancelled) setRounds(all.filter((r) => r.status === PLAYABLE));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load the list.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <div className="centred">
        <div className="round-title">Real or Fake?</div>
        <div className="big">Could not load the list</div>
        <div className="sub">{error}</div>
        <Link className="btn primary" to="/modes" style={{ textDecoration: 'none' }}>Back</Link>
      </div>
    );
  }

  if (!rounds) {
    return <div className="centred"><div className="big">Loading…</div></div>;
  }

  if (rounds.length === 0) {
    return (
      <div className="centred" data-testid="no-series">
        <div className="round-title">Real or Fake?</div>
        <div className="big">Nothing is ready yet</div>
        {/* Names the actual fix, so he can say precisely what he needs rather than "it's broken" —
            but not the people, who change. */}
        <div className="sub">
          Ask Team to set a series to <strong>Ready to Record</strong> and it will show up here.
        </div>
        <Link className="btn primary" to="/modes" style={{ textDecoration: 'none' }}>Back</Link>
      </div>
    );
  }

  return (
    <div className="modes" data-testid="modes-series-list">
      <div className="round-title">Pick a Series</div>
      <div className="pick-grid">
        {rounds.map((round) => (
          <button
            key={round.id}
            className="pick-card"
            onClick={() => navigate(`/modes/series/${round.id}`)}
            data-testid="pick-series"
          >
            <span className="pick-name">{round.title}</span>
            <span className="pick-note">
              {round.counts.total} headline{round.counts.total === 1 ? '' : 's'}
            </span>
          </button>
        ))}
      </div>
      <Link className="modes-back" to="/modes">← Back</Link>
    </div>
  );
}
